import { LOCALE } from "@/config";
import { SECTORS } from "../../../config";
import type { Stock } from "../../../types";

interface UpdateChartProps {
	date: Date;
	d3: any;
	bisect: (
		array: ArrayLike<Stock>,
		x: Date,
		lo?: number,
		hi?: number,
	) => number;
	x: d3.ScalePoint<string>;
	y: d3.ScaleLinear<number, number>;
	z: d3.ScaleOrdinal<string, string>;
	rule: any;
	seriesGroup: any;
}

interface SeriesData {
	key: string;
	visible: boolean;
	values: Stock[];
	value: number;
	percentChange: number;
}

interface TransformData {
	values: Stock[];
}

export function updateChart(props: UpdateChartProps) {
	const { date, d3, bisect, x, y, z, rule, seriesGroup } = props;
	const roundedDate = d3.timeHour.round(date);
	rule.attr("transform", `translate(${x(roundedDate) ?? 0 + 0.5},0)`); // vertical line

	// Update the position of the series
	seriesGroup
		.filter((d: SeriesData) => d.visible) // Only transform visible series
		.attr("transform", ({ values }: TransformData) => {
			const i = bisect(values, date, 0, values.length - 1);

			return `translate(0,${y(1) - y(values[i].value / values[0].value)})`;
		});

	// Update the position of the series labels and count the percent change
	seriesGroup
		.selectAll("text")
		.data((d: SeriesData) => {
			const i = bisect(d.values, date, 0, d.values.length - 1);
			const percentChange = Math.round(
				(d.values[i].value - d.values[0].value) * 100,
			);

			return [
				{
					key: d.key,
					value: d.values[d.values.length - 1].value,
					percentChange,
					visible: d.visible,
				},
			];
		})
		.join("text")
		.attr("fill", (d: SeriesData) => {
			return d.key in SECTORS
				? SECTORS[d.key as keyof typeof SECTORS].color
				: z(d.key);
		})
		.attr("opacity", (d: SeriesData) => (d.visible ? 1 : 0)) // Apply visibility here
		.attr("paint-order", "stroke")
		.attr("stroke", "black")
		.attr("stroke-width", 5)
		.attr("font-size", "12px")
		.attr("x", x.range()[1] + 5)
		.attr("y", (d: SeriesData) => y(d.value))
		.attr("dy", "1em")
		.text((d: SeriesData) => {
			const value = Math.round(d.value * 100) - d.percentChange - 100;
			// Use a numberFormatter to ensure consistent formatting based on locale
			const formatter = new Intl.NumberFormat(LOCALE, {
				style: "decimal",
				signDisplay: "exceptZero",
				maximumFractionDigits: 0,
			});
			return `${d.key}:${formatter.format(value)}%`;
		});
}
